import type { SectionType } from '@tunasong/models'
import { ChordLib, getNoteChromaticIndex, keyDisplayName, ScaleLib } from '@tunasong/music-lib'
import type { Chord, ChordType } from '@tunasong/music-lib'
import {
  defaultMelodyRange,
  melodyNoteSchema,
  standardNoteNames,
  type MelodyNote,
  type MelodyRange,
  type NoteName,
  type Scale,
} from '@tunasong/schemas'
import * as z from 'zod'
import { generatePromptSpec } from '../../generate-prompt.js'
import { chordScale } from '../../music/chord-scale.js'
import type { ContinuationWindow } from '../../music/continuation.js'
import type { AIConfig } from '../../schema/ai-schema.js'

/**
 * Melody suggestion prompt. F.2.1 (`docs/superpowers/specs/2026-05-14-melody-suggestions-v1.md`) wrote one
 * scale-constrained candidate. Melody toolkit M4 adds the singer's range and two or three options, each with a
 * one-line label (`docs/superpowers/plans/2026-10-23-melody-toolkit-plan.md`, step 4.1).
 *
 * `continuation` (M7, `Keep going`) asks instead for options that follow a selected phrase, inside its window. The
 * chords may then be empty (a drone or a standalone idea), and the scale alone constrains the notes.
 *
 * Chord-tone enumeration is computed deterministically server-side (via `ChordLib.getNotes`) so the LLM doesn't need
 * to do music theory.
 */

export interface MelodyPromptChord {
  root: NoteName
  variant: ChordType
  /** Beats from start of section. */
  position: number
  /** Duration in beats. */
  duration: number
}

export interface MelodySuggestionPromptInput {
  scale: Scale
  chords: MelodyPromptChord[]
  bpm?: number
  beatsPerMeasure?: number
  /** F.2.1 ignores this; F.2.3 uses for section-specific contour. */
  sectionType?: SectionType
  style?: string
  /** Inclusive MIDI bounds. Defaults to C3 to B5. */
  range?: MelodyRange
  candidateCount?: number
  /** Keep going: the phrase the options continue, and where the new notes go */
  continuation?: MelodyContinuation
  userConfig?: AIConfig
}

export interface MelodyContinuation {
  phrase: MelodyNote[]
  window: ContinuationWindow
}

/** What the model returns: the options, each with notes and a label. The server repairs and validates them. */
export const melodyGenerationSchema = z.object({
  candidates: z
    .array(
      z.object({
        notes: z.array(melodyNoteSchema).describe('Ordered melody notes'),
        label: z.string().describe('One line describing what this option does'),
      })
    )
    .describe('The melody options, each clearly different from the others'),
})

export type MelodyGeneration = z.infer<typeof melodyGenerationSchema>

/** Scientific pitch name of a MIDI note, sharps for black keys: 55 is G3 */
const midiName = (midi: number) => `${standardNoteNames[midi % 12]}${Math.floor(midi / 12) - 1}`

const formatChordTones = (chord: MelodyPromptChord): string => {
  const chordObj: Chord = { root: chord.root, variant: chord.variant }
  // ChordLib.getNotes sorts the notes alphabetically. Re-order to put the root first
  // so the model sees tones in their conventional ordering (root, third, fifth, ...).
  const tones = Array.from(new Set(ChordLib.getNotes(chordObj)))
  const rootIndex = tones.indexOf(chord.root)
  const rootFirst = rootIndex >= 0 ? [...tones.slice(rootIndex), ...tones.slice(0, rootIndex)] : tones
  return rootFirst.join(', ')
}

/** One chord span: its tones, and the pitches allowed over it (Bowie's per-chord note set, `chordScale`) */
const formatChordLine = (chord: MelodyPromptChord, scale: Scale): string => {
  const tones = formatChordTones(chord)
  const chordName = `${chord.root}${chord.variant}`
  const allowed = chordScale(chordName, scale).join(', ')
  return `    beat ${chord.position} (${chord.duration} beats): ${chordName} — chord tones: ${tones} — allowed: ${allowed}`
}

const formatPhraseNote = (note: MelodyNote) =>
  `    beat ${note.position}: ${note.pitch}${note.octave} (${note.duration})`

const overlaps = (chord: MelodyPromptChord, start: number, end: number) =>
  chord.position < end && chord.position + chord.duration > start

/** "C, E or G" */
const orList = (names: string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : (names[0] ?? '')

/** The strong beats as the theory cues count them (`judgeMelody`): beat 1, and the bar's midpoint in even meters */
const strongBeatsText = (beatsPerMeasure: number) =>
  beatsPerMeasure % 2 === 0 ? `beat 1 and beat ${beatsPerMeasure / 2 + 1} of each bar` : 'beat 1 of each bar'

/**
 * The tonic triad the scale itself contains, by name (Bowie, 2026-10-30): its root, its own 3rd, and its 5th, or the
 * ♭5 when that is the scale's (Locrian). D Dorian is D, F, A; B Locrian is B, D, F. Names, not degree numbers: models
 * misread "the 3rd" in minor keys and modes.
 */
export const keyTriadNames = (scaleNotes: NoteName[]): NoteName[] => {
  const [root] = scaleNotes
  if (root === undefined) {
    return []
  }
  const above = (name: NoteName) => (((getNoteChromaticIndex(name) - getNoteChromaticIndex(root)) % 12) + 12) % 12
  const withInterval = (...intervals: number[]) =>
    intervals.map(interval => scaleNotes.find(name => above(name) === interval)).find(name => name !== undefined)
  return [root, withInterval(3, 4), withInterval(7, 6, 8)].filter((name): name is NoteName => name !== undefined)
}

export default ({
  scale,
  chords,
  bpm = 120,
  beatsPerMeasure = 4,
  sectionType,
  style,
  range = defaultMelodyRange,
  candidateCount = 1,
  continuation,
  userConfig,
}: MelodySuggestionPromptInput) => {
  // ScaleLib.getNotes returns intervals including the upper octave repetition (e.g., C major → [C,D,E,F,G,A,B,C]).
  // Dedupe to the unique pitches the model should pick from.
  const scaleNotes = Array.from(new Set(ScaleLib.getNotes(scale)))
  const sectionLengthBeats = chords.reduce((acc, c) => acc + c.duration, 0)
  const rangeText = `${midiName(range.low)} to ${midiName(range.high)}`
  const options = candidateCount === 1 ? '1 melody option' : `${candidateCount} melody options`
  const distinct =
    candidateCount > 1
      ? ' Make the options clearly different from each other: in rhythm, in contour, or in where they land.'
      : ''
  const entries = candidateCount === 1 ? '1 entry' : `${candidateCount} entries`

  /** A continuation shows only the chords under the phrase and the window */
  const phraseStart = continuation ? Math.min(...continuation.phrase.map(note => note.position)) : 0
  const shownChords = continuation
    ? chords.filter(chord => overlaps(chord, phraseStart, continuation.window.end))
    : chords
  const hasChords = shownChords.length > 0
  const chordLines = hasChords
    ? shownChords.map(chord => formatChordLine(chord, scale)).join('\n')
    : '    none: a drone or a free line, so write over the key alone'
  const pitchRule = hasChords
    ? `- Every note's pitch MUST be one of the pitches allowed over the chord sounding at that beat (the "allowed" list on its chord line: the chord's tones plus the scale's other notes).`
    : `- Every note's pitch MUST be one of the scale notes.`
  const otherNoteRule = `- Use any other note only as an unaccented step between two ${hasChords ? 'allowed' : 'scale'} notes: off the strong beats, no longer than one beat, approached and left by a half or whole step.`
  const strongBeats = strongBeatsText(beatsPerMeasure)
  const strongBeatRule = hasChords
    ? `- Notes on the strong beats (${strongBeats}) should be chord tones of the chord active at that position.`
    : `- Notes on the strong beats (${strongBeats}) should be ${orList(keyTriadNames(scaleNotes))}.`

  const task = continuation
    ? `Continue the phrase below: write ${options}, each following straight on from its last note.${distinct}`
    : `Write ${options} for the following section.${distinct}`
  const lengthLine = continuation
    ? `- Continuation: from beat ${continuation.window.start} to under beat ${continuation.window.end} (${continuation.window.end - continuation.window.start} beats)`
    : `- Section length: ${sectionLengthBeats} beats`
  const phraseBlock = continuation
    ? `
Phrase to continue (the singer's own notes: keep them as they are, and never repeat them in your answer):
${continuation.phrase.map(formatPhraseNote).join('\n')}
`
    : ''
  const positionRule = continuation
    ? `- Every note's position MUST be from ${continuation.window.start} to under ${continuation.window.end}. Return only the new notes.`
    : `- Notes must cover the section: positions range from 0 to under ${sectionLengthBeats}.`
  const phraseLast = continuation?.phrase.reduce((last, note) => (note.position >= last.position ? note : last))
  const shapeRule = phraseLast
    ? `- Carry the phrase's idea on: answer or echo its rhythm or contour, then take it somewhere new.
- Continue in the register of the phrase: start within a 5th of its last note, ${phraseLast.pitch}${phraseLast.octave}.`
    : `- Aim for an arch contour (rise then fall) unless the section asks otherwise.`

  const promptText = `${task}

CONTEXT:
- Key: ${keyDisplayName(scale)}
- Scale notes: [${scaleNotes.join(', ')}]
- Range: ${rangeText} inclusive (scientific pitch, C4 is middle C)
- Meter: ${beatsPerMeasure}/4 at ${bpm} bpm
${lengthLine}
${sectionType ? `- Section type: ${sectionType}` : ''}
${style ? `- Style hint: ${style}` : ''}
${phraseBlock}
Chord progression:
${chordLines}

HARD CONSTRAINTS (must satisfy):
${pitchRule}
${otherNoteRule}
- Every note MUST be inside the range: ${rangeText}.
${positionRule}

SOFT CONSTRAINTS (prefer):
${strongBeatRule}
- Prefer stepwise motion. Resolve leaps over a 6th by step in the opposite direction.
${shapeRule}

Return JSON matching the requested schema: a "candidates" array with ${entries}. Give each option a one-line "label", at most 12 words, that describes what the option does musically, for example "Rises by step, lands on the 3rd of G." Describe it; never call an option correct, best or right.`

  return generatePromptSpec({
    promptText,
    context: 'song:melody',
    userConfig: userConfig?.contexts['song:melody'],
  })
}
